import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useWorkOrder, useDeleteWorkOrder, useUpdateWorkOrder } from '@/components/features/work-orders/api/work-orders';
import { WorkOrderStatusBadge } from '@/components/features/work-orders/work-order-status-badge';
import { WorkOrderPriorityBadge } from '@/components/features/work-orders/work-order-priority-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authenticated/work-orders/$workOrderId')({
  component: WorkOrderDetailPage,
});

function WorkOrderDetailPage() {
  const { t } = useTranslation('workOrders');
  const { workOrderId } = Route.useParams();
  const navigate = useNavigate();

  const { data: workOrder, isLoading, isError } = useWorkOrder(workOrderId);
  const deleteMutation = useDeleteWorkOrder();
  const updateMutation = useUpdateWorkOrder();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(workOrderId);
      setDeleteDialogOpen(false);
      navigate({ to: '/work-orders' });
    } catch {
      // Error handled by mutation toast
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateMutation.mutateAsync({ id: workOrderId, data: { status: newStatus } });
    } catch {
      // Error handled by mutation toast
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-[250px]" />
        <Skeleton className="h-4 w-[400px]" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
        </div>
      </div>
    );
  }

  if (isError || !workOrder) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <p className="text-muted-foreground">
          {t('detail.notFound', { defaultValue: 'Work order not found.' })}
        </p>
        <Button variant="outline" onClick={() => navigate({ to: '/work-orders' })}>
          {t('detail.backToList', { defaultValue: 'Back to Work Orders' })}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{workOrder.title}</h1>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{t('detail.id', { defaultValue: 'ID' })}: {workOrder.id.slice(0, 8)}</span>
            <span className="text-muted-foreground/50">|</span>
            <span>{t('detail.created', { defaultValue: 'Created' })}: {formatDate(workOrder.createdAt)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/work-orders' })}
          >
            {t('detail.back', { defaultValue: 'Back' })}
          </Button>
          <Button
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={deleteMutation.isPending}
          >
            {t('detail.delete', { defaultValue: 'Delete' })}
          </Button>
        </div>
      </div>

      {/* Status and Priority badges */}
      <div className="flex items-center gap-3">
        <WorkOrderStatusBadge status={workOrder.status} />
        <WorkOrderPriorityBadge priority={workOrder.priority} />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Description */}
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.descriptionTitle', { defaultValue: 'Description' })}</CardTitle>
          </CardHeader>
          <CardContent>
            {workOrder.description ? (
              <p className="text-sm whitespace-pre-wrap">{workOrder.description}</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('detail.noDescription', { defaultValue: 'No description provided.' })}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Details */}
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.detailsTitle', { defaultValue: 'Details' })}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('detail.status', { defaultValue: 'Status' })}</span>
              <WorkOrderStatusBadge status={workOrder.status} />
            </div>
            <Separator />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('detail.priority', { defaultValue: 'Priority' })}</span>
              <WorkOrderPriorityBadge priority={workOrder.priority} />
            </div>
            <Separator />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('detail.asset', { defaultValue: 'Asset' })}</span>
              <span>{workOrder.asset?.name || '-'}</span>
            </div>
            <Separator />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('detail.scheduledStart', { defaultValue: 'Scheduled Start' })}</span>
              <span>{formatDate(workOrder.scheduledStart)}</span>
            </div>
            <Separator />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('detail.completedAt', { defaultValue: 'Completed At' })}</span>
              <span>{formatDate(workOrder.completedAt)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assigned Users */}
      <Card>
        <CardHeader>
          <CardTitle>{t('detail.assigneesTitle', { defaultValue: 'Assigned Users' })}</CardTitle>
        </CardHeader>
        <CardContent>
          {workOrder.workOrderUsers && workOrder.workOrderUsers.length > 0 ? (
            <div className="flex flex-wrap gap-3">
              {workOrder.workOrderUsers.map((wu) => (
                <UserAvatar
                  key={wu.userId}
                  user={wu.user}
                  size="h-8 w-8"
                  showName
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('detail.noAssignees', { defaultValue: 'No users assigned.' })}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Linked Assets */}
      {workOrder.workOrderAssets && workOrder.workOrderAssets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.assetsTitle', { defaultValue: 'Linked Assets' })}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {workOrder.workOrderAssets.map((woa, i) => (
                <div key={`${woa.assetId}-${i}`} className="flex items-center justify-between text-sm">
                  <span>{woa.asset?.name || woa.assetId}</span>
                  {woa.isPrimary && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                      {t('detail.primary', { defaultValue: 'Primary' })}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>{t('detail.timelineTitle', { defaultValue: 'Timeline' })}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('detail.createdAt', { defaultValue: 'Created' })}</span>
            <span>{formatDate(workOrder.createdAt)}</span>
          </div>
          {workOrder.assignedAt && (
            <>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('detail.assignedAt', { defaultValue: 'Assigned' })}</span>
                <span>{formatDate(workOrder.assignedAt)}</span>
              </div>
            </>
          )}
          {workOrder.completedAt && (
            <>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('detail.completedAt', { defaultValue: 'Completed' })}</span>
                <span>{formatDate(workOrder.completedAt)}</span>
              </div>
            </>
          )}
          <Separator />
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('detail.updatedAt', { defaultValue: 'Last Updated' })}</span>
            <span>{formatDate(workOrder.updatedAt)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('detail.deleteConfirmTitle', { defaultValue: 'Delete Work Order' })}
            </DialogTitle>
            <DialogDescription>
              {t('detail.deleteConfirmDescription', {
                defaultValue: 'Are you sure you want to delete this work order? This action cannot be undone.',
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteMutation.isPending}
            >
              {t('detail.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? t('detail.deleting', { defaultValue: 'Deleting...' })
                : t('detail.confirmDelete', { defaultValue: 'Delete' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
